<template>
    <v-card class="ma-3 pa-5" v-if="activeProbe">
        <h3>Locations</h3>
        <v-simple-table dense>
            <template v-slot:default>
                <tbody>
                <tr>
                    <td>
                        <h4>Change location</h4>
                    </td>
                    <td>
                        <v-text-field v-model="newLocation.latitude" type="number" label="Latitude"/>
                    </td>
                    <td>
                        <v-text-field v-model="newLocation.longitude" type="number" label="Longitude"/>
                    </td>
                    <td>
                        <v-menu
                            v-model="dateMenu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    label="Start date"
                                    :value="formattedDate"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                first-day-of-week="1"
                                v-model="newLocation.date"
                                @input="dateMenu = false"
                            ></v-date-picker>
                        </v-menu>
                    </td>
                    <td class="text-right">
                        <v-btn @click="handleSaveLocationClick" small color="primary">Save</v-btn>
                    </td>
                </tr>
                </tbody>
            </template>
        </v-simple-table>

        <v-simple-table>
            <template v-slot:default>
                <thead>
                <tr>
                    <th class="text-left">
                        #
                    </th>
                    <th class="text-left">
                        ID
                    </th>
                    <th class="text-left">
                        Name
                    </th>
                    <th class="text-left" v-if="cultures.length > 0">
                        Culture
                    </th>
                    <th class="text-left" v-if="tillageMethods.length > 0">
                        Tillage method
                    </th>
                    <th class="text-left">
                        Company
                    </th>
                    <th class="text-left">
                        Client nr
                    </th>
                    <th class="text-left">
                        Field
                    </th>
                    <th class="text-left">
                        Soil Type
                    </th>
                    <th class="text-left">
                        Sector
                    </th>
                    <th class="text-left">
                        Latitude
                    </th>
                    <th class="text-left">
                        Longitude
                    </th>
                    <th class="text-left">
                        Country
                    </th>
                    <th class="text-left">
                        Region
                    </th>
                    <th class="text-left">
                        Date from
                    </th>
                    <th class="text-left">
                        Date to
                    </th>
                    <th class="text-left">
                        Season
                    </th>
                    <th class="text-left">
                        Users
                    </th>
                    <th width="5%" class="text-right"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(location, idx) in locations" :key="`location_${location.id}`" :class="{'merged-station-location': !isMainLocation(location)}">
                    <td v-if="isMainLocation(location)">{{ idx + 1 }}</td>
                    <td v-else>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-icon color="warning" class="ml-2" v-on="on" v-bind="attrs">mdi-alert</v-icon>
                            </template>
                            <span>This location is merged and this station is not the main station</span>
                        </v-tooltip>
                    </td>
                    <td>{{location.id}}</td>
                    <td class="base-min-width">
                        <v-text-field label="Name" v-model="location.alias"/>
                    </td>
                    <td v-if="cultures.length > 0" class="base-min-width">
                        <v-select
                            item-value="id"
                            item-text="locale.en_culture"
                            :items="cultures"
                            label="Culture"
                            v-model="location.culture_id"
                        />
                    </td>
                    <td v-if="tillageMethods.length > 0" class="base-min-width">
                        <v-select
                            item-value="id"
                            item-text="name"
                            :items="tillageMethods"
                            label="Tillage method"
                            v-model="location.tillage_method_id"
                        />
                    </td>
                    <td class="base-min-width">
                        <v-select v-model="location.location_company_id"
                                  :items="companies"
                                  item-text="company"
                                  item-value="id"
                                  label="Company"/>
                    </td>
                    <td class="base-min-width">
                        <v-text-field label="Client nr" v-model="location.client_nr"/>
                    </td>
                    <td v-if="location.field_list.length > 0" class="base-min-width">
                        <v-select
                            item-value="id"
                            item-text="name"
                            :items="location.field_list"
                            label="Field"
                            v-model="location.field_id"
                            :clearable="true"
                            @change="handleFieldChange($event, location)"
                        />
                    </td>
                    <td v-if="activeProbe.soil_type" class="base-min-width">
                        <v-select
                            item-value="id"
                            item-text="name"
                            :items="soilTypes"
                            label="Soil Type"
                            v-model="location.soil_type_id"
                            :clearable="false"
                        />
                    </td>
                    <td class="base-min-width">
                        <v-select
                            item-value="id"
                            item-text="name"
                            :items="sectors"
                            label="Sector"
                            v-model="location.sector_id"
                            :clearable="false"
                        />
                    </td>
                    <td :title="location.latitude">
                        <span>{{ location.latitude }}</span>
                    </td>
                    <td :title="location.longitude">{{ location.longitude }}</td>
                    <td :title="location.country">{{ location.country_code || '-' }}</td>
                    <td :title="location.region">{{ location.region }}</td>
                    <td>
                        <v-menu
                            v-model="location.show_date_from_menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="formatDate(location.start_date)"
                                    prepend-icon="mdi-calendar"
                                    class="min-width-115"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                first-day-of-week="1"
                                v-model="location.start_date"
                                @input="location.show_date_from_menu = false"
                            ></v-date-picker>
                        </v-menu>
                    </td>
                    <td>
                        <v-menu
                            v-model="location.show_date_to_menu"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="formatDate(location.end_date)"
                                    @change="endDateChange($event, location)"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    class="min-width-115"
                                    v-bind="attrs"
                                    :clearable="idx === activeProbe.locations.length - 1"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                first-day-of-week="1"
                                v-model="location.end_date"
                                @input="location.show_date_to_menu = false"
                            ></v-date-picker>
                        </v-menu>
                    </td>
                    <td class="probes-locations-dropdown-menu base-min-width">
                        <v-select
                            v-model="location.season_id"
                            hide-details
                            outlined
                            dense
                            item-text="title"
                            item-value="id"
                            label="Season"
                            :items="seasons"
                        />
                    </td>
                    <td class="probes-locations-dropdown-menu base-min-width">
                        <v-autocomplete
                            v-model="location.users"
                            :items="noAdminUsers"
                            item-text="name_email"
                            item-value="id"
                            outlined
                            dense
                            chips
                            small-chips
                            label="Users"
                            hide-details
                            multiple
                            return-object
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip small v-if="index === 0">
                                    <span>{{ item.name }}</span>
                                </v-chip>
                                <span v-if="index === 1" class="grey--text text-caption"
                                >(+{{ location.users.length - 1 }} others)</span
                                >
                            </template>
                        </v-autocomplete>
                    </td>
                    <td class="text-right">
                        <v-row justify="center" class="min-width-115 action-buttons">
                            <v-tooltip bottom v-if="user.role === 'super_admin'">
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="removeLocation(location)" small color="primary" icon v-on="on">
                                        <v-icon>mdi-trash-can-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>Remove</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="openInMaps(location)" small color="primary" icon v-on="on">
                                        <v-icon>mdi-earth</v-icon>
                                    </v-btn>
                                </template>
                                <span>Open in google maps</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="saveLocation(location)" small color="primary" icon v-on="on">
                                        <v-icon>mdi-content-save</v-icon>
                                    </v-btn>
                                </template>
                                <span>Save</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        @click="handleDuplicateLocationClick(location)"
                                        small
                                        color="primary"
                                        icon
                                        v-on="on"
                                    >
                                        <v-icon>mdi-content-duplicate</v-icon>
                                    </v-btn>
                                </template>
                                <span>Duplicate</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="openGraphs(location)" small color="primary" icon v-on="on">
                                        <v-icon>mdi-chart-bell-curve-cumulative</v-icon>
                                    </v-btn>
                                </template>
                                <span>Graph</span>
                            </v-tooltip>
                            <v-tooltip bottom v-if="isAdmin">
                                <template v-slot:activator="{ on }">
                                    <v-btn @click="openOldGraphs(location)" small color="primary" icon v-on="on">
                                        <v-icon>mdi-chart-bell-curve-cumulative</v-icon>
                                    </v-btn>
                                </template>
                                <span>Old graph</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn
                                        @click="openStationLocation(location)"
                                        @click.middle.exact="openStationLocation(location, true)"
                                        small
                                        color="primary"
                                        icon
                                        v-on="on"
                                    >
                                        <v-icon>mdi-open-in-new</v-icon>
                                    </v-btn>
                                </template>
                                <span>Details</span>
                            </v-tooltip>
                        </v-row>
                    </td>
                </tr>
                </tbody>
            </template>
        </v-simple-table>
    </v-card>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import moment from 'moment';

export default {
    name: 'StationLocationCard',
    data() {
        return {
            dateMenu: false,
        };
    },
    computed: {
        ...mapState('probe', ['activeProbe', 'newLocation', 'companies', 'seasons', 'tillageMethods', 'companies', 'sectors', 'activeStations']),
        ...mapState('app', ['users']),
        ...mapState('auth', ['user']),
        ...mapState('culture', ['cultures']),
        ...mapState('soil', ['soilTypes']),
        ...mapGetters('auth', ['isAdmin']),
        ...mapGetters('app', ['noAdminUsers']),

        formattedDate() {
            if (this.newLocation.date) {
                return moment(this.newLocation.date).format('DD.MM.YYYY');
            }
            return null;
        },

        locations() {
            let locations = []

            this.activeStations.forEach(ac => {
                locations = [...locations, ...ac.locations]
            })

            return locations.sort((a, b) => {
                if (!a.start_date) return 1;
                if (!b.start_date) return -1;
                return a.start_date.localeCompare(b.start_date);
            });
        }
    },
    async mounted() {
        await this.fetchTillageMethods();
        await this.fetchSectors();
        await this.fetchLocationCompany();
        await this.fetchSeasons();
    },
    methods: {
        ...mapActions('probe', [
            'createLocation',
            'updateProbeLocation',
            'removeProbeLocation',
            'fetchLocationCompany',
            'fetchSeasons',
            'fetchTillageMethods',
            'duplicateLocation',
            'fetchSectors',
            'fetchProbe'
        ]),
        ...mapActions('culture', ['fetchCulture']),
        openInMaps(location) {
            let url = 'https://maps.google.com/?t=k&q=' + location.latitude + ',' + location.longitude;
            window.open(url, '_blank');
        },
        handleSaveLocationClick() {
            if (!this.newLocation.latitude || !this.newLocation.longitude || !this.newLocation.date) {
                alert('Please fill all fields!');
                return;
            }
            this.createLocation();
        },
        // add/remove field owner to/from location users if field is selected/deselected
        handleFieldChange(fieldId, location) {
            const selectedFieldId = fieldId ? parseInt(fieldId) : -1;
            const user = location.field_list.filter(f => f.id === selectedFieldId)[0]?.created_by_user;
            const selectedUserId = user ? user.id : -1;
            let users = location.users?.filter(u => !u.selected && u.id !== selectedUserId) ?? []; // filter out currently selected user
            if (user) {
                user.selected = true;
                location.users = [...users, user];
            } else {
                location.users = [...users];
            }
        },
        saveLocation(location) {
            if (
                !location?.alias ||
                !location?.culture_id ||
                !location?.location_company_id ||
                !location?.season_id ||
                !location?.sector_id
            ) {
                alert('Please fill name, culture, company, sector and season field!');
                return;
            }
            this.updateProbeLocation({probeId: location.station_id, location});
        },
        async removeLocation(location) {
            if (confirm('Would you like to remove this location?')) {
                await this.removeProbeLocation({probeId: location.station_id, locationId: location.id});
            }
        },
        formatDate(date) {
            if (date) {
                return moment(date).format('DD.MM.YYYY');
            }
            return '-';
        },
        openGraphs(location) {
            let url = new URL(`${process.env.VUE_APP_APP_URL}/graph-v2/${location.id}`);
            window.open(url, '_blank');
        },
        openOldGraphs(location) {
            let alias = location.alias ? location.alias : this.activeProbe.alias;
            let url = new URL(`${process.env.VUE_APP_APP_URL}/graph/${location.id}`);
            url.searchParams.set('alias', alias);
            window.open(url, '_blank');
        },
        endDateChange(e, location) {
            if (!e) {
                location.end_date = null;
            }
        },
        async handleDuplicateLocationClick(location) {
            if (!location.latitude || !location.longitude) {
                alert('Latitude and longitude required');
                return;
            }
            await this.duplicateLocation({activeProbeId: location.station_id, locationId: location.id});
            await this.fetchProbe(this.$route.params.id)
        },
        openStationLocation(location, newWindow = false) {
            if (newWindow) {
                const path = this.$router.resolve({
                    name: 'station-location',
                    params: {probeId: location.station_id, locationId: location.id},
                });
                return window.open(path.href, '_blank');
            }
            this.$router.push({
                name: 'station-location',
                params: {probeId: location.station_id, locationId: location.id},
            });
        },
        isMainLocation(location) {
            return this.activeProbe.id === location.station_id;
        }
    },
};
</script>

<style scoped>
.text-right {
    padding: 0 !important;
    width: 7%;
}

.probes-locations-dropdown-menu {
    padding: 0 8px !important;
}

.min-width-115 {
    min-width: 115px !important;
}

.base-min-width {
    min-width: 115px;
}

.action-buttons {
    flex-wrap: unset !important;
    margin-left: 0;
}

.merged-station-location {
    background-color: #e1e1e1;
}
</style>
